/**
 * Canonical authorization constants — SINGLE SOURCE OF TRUTH.
 *
 * Every file that needs to classify a resource as "experience-shell" vs
 * "plugin-owned" MUST import from here. Do NOT duplicate these lists.
 *
 * When adding a new experience-shell resource type, add it to
 * EXPERIENCE_RESOURCE_TYPES below and it will propagate to:
 *   - authz-preload.ts (pdp_application inference for preload)
 *   - authz-operation-refs.ts (operation_ref_id application prefix)
 *   - plugin-auth SDK (via globalThis.__authzConstants at runtime)
 */

/**
 * Resource types that belong to the "experience" application scope.
 *
 * Matching is prefix-based: "experience.plugin" starts with "experience."
 * so it matches. "entitlement-ledger" matches none, so it's treated as
 * a plugin resource whose pdp_application equals its own name.
 *
 * Entries must be lowercase. Matching is case-insensitive.
 */
export const EXPERIENCE_RESOURCE_TYPES: readonly string[] = Object.freeze([
  'experience.',
  'experience:',
  'dashboard',
  'profile',
  'navigation',
  'help',
  'notifications',
  'pages',
  'workflows',
  'tasks',
  'page.widget',
  'plugin.widget',
  'home',
  'catalog',
  'settings',
  'user',
  'admin-panel',
  'collection',
  'theme',
]);

/**
 * Test whether a resource type belongs to the experience shell.
 * Case-insensitive prefix match against EXPERIENCE_RESOURCE_TYPES.
 */
export function isExperienceResource(resourceType: string): boolean {
  const lower = resourceType.toLowerCase();
  return EXPERIENCE_RESOURCE_TYPES.some(
    prefix => lower === prefix || lower.startsWith(prefix),
  );
}

/**
 * Plugin-owned resource types whose bare name does NOT encode the owning
 * application, so prefix inference would mis-route them to a non-existent
 * policy set (fail-closed deny).
 *
 * Operations Manager uses bare nouns ("groups", "onboarding", "triage", …)
 * that the PDP policy set "operations-manager" authorizes (grants keyed
 * op:operations-manager:groups.*, onboarding.*, triage.manage, etc.).
 * Without this map, inferPdpApplication mis-routes to a non-existent app
 * and the op_ref fails closed (e.g. op:groups:groups.view).
 *
 * Keys must be lowercase. Matching is exact (full resource type).
 */
export const RESOURCE_TYPE_APP_OVERRIDES: Readonly<Record<string, string>> = Object.freeze({
  groups: 'operations-manager',
  'groups.members': 'operations-manager',
  'groups.changes': 'operations-manager',
  onboarding: 'operations-manager',
  triage: 'operations-manager',
});

/**
 * PDP-enforced format for pdp_application values: ^[a-z0-9-]{3,64}$
 * No dots, colons, underscores, or uppercase characters.
 */
const PDP_APP_REGEX = /^[a-z0-9-]{3,64}$/;

/**
 * Sanitize a pdp_application value to match PDP's required format.
 * Replaces dots/colons/underscores with hyphens, lowercases, trims
 * leading/trailing hyphens, and enforces min length 3.
 */
export function sanitizePdpApplication(value: string): string {
  const sanitized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (sanitized.length < 3) {
    console.warn(
      `[AuthZ] pdp_application "${value}" sanitized to <3 chars ("${sanitized}"); ` +
      `falling back to "experience". This may indicate a misconfigured plugin manifest.`,
    );
    return 'experience';
  }
  if (sanitized.length > 64) return sanitized.slice(0, 64);
  return sanitized;
}

/**
 * Derive pdp_application from a resource type.
 *
 * - Shell resources → "experience"
 * - Colon-delimited plugin resources (e.g. "theme-studio:editor") → namespace
 *   prefix before the colon ("theme-studio")
 * - Plain plugin resources (e.g. "entitlement-ledger") → the type itself
 *
 * The result always matches the PDP format regex ^[a-z0-9-]{3,64}$.
 */
export function inferPdpApplication(resourceType: string): string {
  if (isExperienceResource(resourceType)) return 'experience';
  const override = RESOURCE_TYPE_APP_OVERRIDES[(resourceType || '').toLowerCase()];
  if (override) return override;
  const raw = resourceType || 'experience';
  const colonIdx = raw.indexOf(':');
  const candidate = colonIdx > 0 ? raw.substring(0, colonIdx) : raw;
  if (PDP_APP_REGEX.test(candidate)) return candidate;
  return sanitizePdpApplication(candidate);
}

/**
 * Normalize a string into a valid operation_ref_id token segment.
 * Output contains only [a-z0-9._-], with collapsed runs and trimmed edges.
 */
export function normalizeOperationToken(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9._-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^[_.\-]+|[_.\-]+$/g, '');
}

// Publish to globalThis so the plugin-auth SDK (separate bundle) can read
// these at runtime without duplicating the list.
(globalThis as any).__authzConstants = {
  EXPERIENCE_RESOURCE_TYPES,
  RESOURCE_TYPE_APP_OVERRIDES,
  isExperienceResource,
  inferPdpApplication,
  sanitizePdpApplication,
  normalizeOperationToken,
};
